import { Link } from "react-router-dom";
import BrandName from "@/components/BrandName";

const COMPANY = "OrbitRank";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="container py-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold">
            <span className="w-7 h-7 rounded-md bg-primary/15 flex items-center justify-center">🚀</span>
            <BrandName />
          </Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">← Back to home</Link>
        </div>
      </header>

      <main className="container max-w-3xl py-16">
        <p className="text-base font-semibold text-foreground mb-6">OrbitRank is operated by Noam Cohen.</p>
        <h1 className="text-4xl font-bold mb-2">Terms of Service</h1>
        <p className="text-sm text-muted-foreground mb-10">Last updated: April 17, 2026</p>

        <div className="space-y-6 text-[15px] leading-relaxed text-muted-foreground">
          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">1. Who you are contracting with</h2>
            <p>
              These Terms of Service ("Terms") form a binding agreement between you and {COMPANY}
              (trading as "OrbitRank", "we", "us") covering your use of the OrbitRank website,
              applications, and SaaS service (the "Service").
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">2. Acceptance</h2>
            <p>
              By creating an account, accessing, or continuing to use the Service, you agree to these Terms.
              If you are using the Service on behalf of an organisation, you represent that you have authority
              to bind that organisation. You must be of legal age to form a binding contract.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">3. The Service</h2>
            <p>
              OrbitRank is an autonomous SEO platform that analyses your website, generates and publishes
              content, tracks search rankings, monitors competitors, and submits your site to relevant
              directories. Specific features depend on your subscription plan.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">4. Account & credentials</h2>
            <p>
              You must provide accurate information during signup and keep it up to date. You are responsible
              for safeguarding your account credentials and for any activity that occurs under your account.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">5. Acceptable use</h2>
            <p>You must not:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Use the Service unlawfully, fraudulently, or to send spam.</li>
              <li>Infringe intellectual property rights or publish content you don't have rights to.</li>
              <li>Probe, scan, scrape, or interfere with the Service or its security.</li>
              <li>Upload malware or attempt to gain unauthorised access.</li>
              <li>Resell, redistribute, or sublicense the Service without our written consent.</li>
              <li>Reverse engineer, decompile, or circumvent technical limits.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">6. Generated content & AI outputs</h2>
            <p>
              The Service uses AI to generate SEO content. You are responsible for reviewing AI-generated
              outputs before publication, ensuring they are accurate, lawful, and that you have rights to any
              inputs you provide. We may filter, restrict, or remove outputs at our discretion. Outputs are
              not professional advice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">7. Intellectual property</h2>
            <p>
              We retain all rights, title, and interest in the Service, including our software, branding, and
              documentation. We grant you a limited, non-exclusive, non-transferable right to use the Service
              during your subscription, within the limits of your plan.
            </p>
            <p>
              You retain ownership of content you submit. You grant us a limited licence to host and process
              your content solely to provide the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">8. Subscriptions, billing, and payments</h2>
            <p>
              Our order process is conducted by our online reseller Paddle.com. <strong className="text-foreground">Paddle is the
              Merchant of Record for all our orders. Paddle provides all customer service inquiries and
              handles returns.</strong>
            </p>
            <p>
              For payment, billing, tax, cancellation, and refund mechanics, please refer to Paddle's Buyer
              Terms at <a className="text-primary underline" href="https://www.paddle.com/legal/checkout-buyer-terms" target="_blank" rel="noreferrer">paddle.com/legal/checkout-buyer-terms</a>.
              Subscriptions renew automatically at the end of each billing cycle until cancelled. You may
              cancel at any time and retain access until the end of your current billing period.
            </p>
            <p>
              See also our <Link to="/refund-policy" className="text-primary underline">Refund Policy</Link>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">9. Free trial</h2>
            <p>
              New accounts may receive a 7-day free trial. At the end of the trial, continued access requires
              an active paid subscription.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">10. Service availability</h2>
            <p>
              We work hard to keep the Service available and reliable, but we do not guarantee uninterrupted
              or error-free operation. Maintenance, third-party outages, and unforeseen events may
              temporarily affect availability.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">11. Suspension & termination</h2>
            <p>
              We may suspend or terminate your access for material breach of these Terms, non-payment,
              security or fraud risk, or repeated policy violations. Upon termination, your right to use the
              Service ends immediately. You may export your data within 30 days of termination, after which
              we may delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">12. Warranties & disclaimers</h2>
            <p>
              To the fullest extent permitted by law, the Service is provided "as is" and "as available". We
              disclaim all implied warranties, including merchantability, fitness for a particular purpose,
              and non-infringement. We do not guarantee specific search rankings or business outcomes.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">13. Limitation of liability</h2>
            <p>
              To the fullest extent permitted by law, our aggregate liability arising out of or related to the
              Service is capped at the fees you paid us in the 12 months preceding the claim. We are not
              liable for indirect, consequential, special, or incidental damages, including loss of profits,
              data, or goodwill. Nothing in these Terms excludes liability for fraud, death, or personal
              injury caused by negligence where required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">14. Indemnity</h2>
            <p>
              You agree to indemnify {COMPANY} against claims, damages, and costs arising from your content,
              your use of the Service in violation of these Terms, or your unlawful conduct.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">15. Changes to the Service or Terms</h2>
            <p>
              We may modify the Service or these Terms from time to time. Material changes will be notified
              by email or in-app. Continued use after changes take effect constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">16. Governing law</h2>
            <p>
              These Terms are governed by the laws of {COMPANY}'s jurisdiction of incorporation. Disputes
              will be resolved in the competent courts of that jurisdiction, unless local mandatory law
              provides otherwise.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">17. Assignment & force majeure</h2>
            <p>
              You may not assign these Terms without our consent. We may assign them in connection with a
              merger, acquisition, or sale of assets. Neither party is liable for delays caused by events
              beyond reasonable control.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-foreground mt-8 mb-3">18. Contact</h2>
            <p>
              Questions about these Terms? Contact {COMPANY} through the chat widget on our website or your
              account support channel.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-border py-8 text-center text-xs text-muted-foreground">
        <div className="container flex flex-wrap justify-center gap-6">
          <Link to="/privacy-policy" className="hover:text-foreground">Privacy</Link>
          <Link to="/terms-of-service" className="hover:text-foreground">Terms</Link>
          <Link to="/refund-policy" className="hover:text-foreground">Refunds</Link>
        </div>
        <div className="mt-3">OrbitRank © 2026</div>
      </footer>
    </div>
  );
}
